import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  PhoneCall,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Phone,
  Search,
  RefreshCw,
  TrendingUp,
  MessageSquare,
  Plus,
  ExternalLink,
  Download,
  Eye,
  Sparkles,
  Users,
  Edit3,
  ArrowLeft,
  DollarSign,
  UserCheck,
  Check,
  Layers,
} from "lucide-react";
import { Input, Select, DatePicker, InputNumber, message } from "antd";
import dayjs from "dayjs";
import "antd/dist/reset.css";
import { useTheme } from "../context/ThemeContext";
import { fintechApi } from "../services/api";

const formatINR = (val) => {
  if (val === undefined || val === null || isNaN(val)) return "₹0";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(val);
};

// Blob ko file download me badalne ka chhota helper — dono export yahi use karte hain
const triggerBlobDownload = (blob, fileName) => {
  if (!blob || typeof blob.size !== "number" || blob.size === 0) {
    throw new Error("Server ne khali file bheji hai");
  }
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export default function CollectionDashboard() {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const primaryColor = currentTheme?.color || "#2e856e";

  // Active View Tab: "overview" (Dashboard & Queue) | "create-task" (Full Page Form) | "log-call" (Full Page Disposition)
  const [activeTab, setActiveTab] = useState("overview");

  const [loading, setLoading] = useState(false);
  const [submittingNewTask, setSubmittingNewTask] = useState(false);
  const [submittingContact, setSubmittingContact] = useState(false);
  const [submittingStatus, setSubmittingStatus] = useState(false);
  const [exportingReport, setExportingReport] = useState(false);

  // Top Stats
  const [stats, setStats] = useState({
    totalOverdue: 0,
    collectedToday: 0,
    pendingTasks: 0,
    promiseToPay: 0,
  });

  // Aging Buckets
  const [buckets, setBuckets] = useState([
    { bucket: "0-30 Days (Bucket 0)", count: 0, overdue: 0, collected: 0, pending: 0, par: "0%" },
    { bucket: "31-60 Days (Bucket 1)", count: 0, overdue: 0, collected: 0, pending: 0, par: "0%" },
    { bucket: "61-90 Days (Bucket 2)", count: 0, overdue: 0, collected: 0, pending: 0, par: "0%" },
    { bucket: "90+ Days (NPA / Default)", count: 0, overdue: 0, collected: 0, pending: 0, par: "0%" },
  ]);

  // Agent Performance
  const [agentPerformance, setAgentPerformance] = useState([]);

  // Collection Tasks Queue
  const [tasks, setTasks] = useState([]);

  // Filter & Search States
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [bucketFilter, setBucketFilter] = useState("ALL");
  const [agentFilter, setAgentFilter] = useState("ALL");

  // Selected Task for Inline Log Call or Status
  const [selectedTaskForCall, setSelectedTaskForCall] = useState(null);
  const [contactForm, setContactForm] = useState({
    disposition: "PROMISED_TO_PAY",
    contactMode: "PHONE_CALL",
    nextFollowUpDate: null,
    ptpAmount: 0,
    remarks: "",
  });

  // Selected Task for Inline Status Update
  const [selectedTaskForStatus, setSelectedTaskForStatus] = useState(null);
  const [statusForm, setStatusForm] = useState({
    status: "PENDING",
    remarks: "",
  });

  // History Task
  const [historyTask, setHistoryTask] = useState(null);

  // Full Page New Task Form State
  const [newTaskForm, setNewTaskForm] = useState({
    borrower: "",
    phone: "",
    altPhone: "",
    email: "",
    loanAccount: "",
    overdueAmount: "",
    principalOverdue: "",
    interestOverdue: "",
    dueSince: "15 Days (Bucket 0)",
    agent: "",
    priority: "MEDIUM",
    status: "PENDING",
    remarks: "",
  });

  const fetchCollectionData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, tasksRes] = await Promise.allSettled([
        fintechApi.getCollectionDashboard(),
        fintechApi.getCollectionTasks(),
      ]);

      if (dashRes.status === "fulfilled" && dashRes.value) {
        const val = dashRes.value.data || dashRes.value;
        if (val) {
          setStats({
            totalOverdue: val.totalOverdue ?? val.total_overdue ?? val.overdueAmount ?? 0,
            collectedToday: val.collectedToday ?? val.collected_today ?? val.todayCollected ?? 0,
            pendingTasks: val.pendingTasks ?? val.pending_tasks ?? val.tasksCount ?? 0,
            promiseToPay: val.promiseToPay ?? val.promise_to_pay ?? val.ptpCount ?? 0,
          });

          if (Array.isArray(val.buckets)) {
            setBuckets(val.buckets);
          }
          if (Array.isArray(val.agentPerformance || val.agents)) {
            setAgentPerformance(val.agentPerformance || val.agents);
          }
        }
      }

      if (tasksRes.status === "fulfilled" && tasksRes.value) {
        const list = Array.isArray(tasksRes.value)
          ? tasksRes.value
          : tasksRes.value.data || tasksRes.value.tasks;
        if (Array.isArray(list)) {
          setTasks(list);
        }
      }
    } catch (e) {
      console.warn("API response error / using default data:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCollectionData();
  }, [fetchCollectionData]);

  // ➕ POST Call: Create New Collection Task (Full Single Page Form Submit)
  const handleCreateTask = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!newTaskForm.borrower?.trim()) {
      message.error("Please enter borrower name");
      return;
    }
    if (!newTaskForm.phone?.trim()) {
      message.error("Please enter borrower phone number");
      return;
    }
    if (!newTaskForm.loanAccount?.trim()) {
      message.error("Please enter loan account number");
      return;
    }
    if (!newTaskForm.overdueAmount) {
      message.error("Please enter overdue amount");
      return;
    }

    setSubmittingNewTask(true);
    try {
      const rawLoanAc = newTaskForm.loanAccount.trim();
      const cleanNumericId = Number(rawLoanAc.replace(/[^0-9]/g, "")) || Number(rawLoanAc) || 1;

      const payload = {
        loanAccountId: cleanNumericId,
        loanId: cleanNumericId,
        loanAccount: rawLoanAc,
        borrower: newTaskForm.borrower.trim(),
        borrowerName: newTaskForm.borrower.trim(),
        phone: newTaskForm.phone.trim(),
        altPhone: newTaskForm.altPhone?.trim() || "",
        email: newTaskForm.email?.trim() || "",
        overdueAmount: Number(newTaskForm.overdueAmount),
        principalOverdue: Number(newTaskForm.principalOverdue || 0),
        interestOverdue: Number(newTaskForm.interestOverdue || 0),
        dueSince: newTaskForm.dueSince,
        priority: newTaskForm.priority || "MEDIUM",
        agent: newTaskForm.agent || "Unassigned",
        assignedAgent: newTaskForm.agent || "Unassigned",
        status: newTaskForm.status || "PENDING",
        lastRemarks: newTaskForm.remarks || "Task created & assigned",
        remarks: newTaskForm.remarks || "",
      };

      await fintechApi.createCollectionTask(payload);
      message.success("Collection task created & assigned successfully!");

      // Reset Form & Switch back to overview tab
      setNewTaskForm({
        borrower: "",
        phone: "",
        altPhone: "",
        email: "",
        loanAccount: "",
        overdueAmount: "",
        principalOverdue: "",
        interestOverdue: "",
        dueSince: "15 Days (Bucket 0)",
        agent: "",
        priority: "MEDIUM",
        status: "PENDING",
        remarks: "",
      });
      setActiveTab("overview");
      fetchCollectionData();
    } catch (e) {
      console.error("createCollectionTask error:", e);
      message.error(e?.message || "Failed to create collection task");
    } finally {
      setSubmittingNewTask(false);
    }
  };

  // 📝 POST Call: Record Contact Attempt & Disposition
  const handleSaveContactAttempt = async () => {
    if (!selectedTaskForCall) return;
    setSubmittingContact(true);
    try {
      const payload = {
        taskId: selectedTaskForCall.id,
        disposition: contactForm.disposition,
        contactMode: contactForm.contactMode,
        remarks: contactForm.remarks || contactForm.disposition,
        ptpAmount: Number(contactForm.ptpAmount || 0),
        nextFollowUpDate: contactForm.nextFollowUpDate
          ? (contactForm.nextFollowUpDate.format ? contactForm.nextFollowUpDate.format("YYYY-MM-DD") : contactForm.nextFollowUpDate)
          : null,
      };

      await fintechApi.recordContactAttempt(selectedTaskForCall.id, payload);
      message.success("Contact attempt feedback logged successfully!");

      setTasks((prev) =>
        prev.map((t) =>
          t.id === selectedTaskForCall.id
            ? {
                ...t,
                status: contactForm.disposition === "PROMISED_TO_PAY" ? "PROMISE_TO_PAY" : "CALLED",
                lastRemarks: contactForm.remarks || contactForm.disposition,
                history: [
                  ...(t.history || []),
                  {
                    date: dayjs().format("DD/MM/YYYY, hh:mm A"),
                    disposition: contactForm.disposition,
                    remarks: contactForm.remarks,
                    ptpAmount: contactForm.ptpAmount,
                  },
                ],
              }
            : t
        )
      );

      setSelectedTaskForCall(null);
      setActiveTab("overview");
      fetchCollectionData();
    } catch (e) {
      // Save fail hua to form khula rehne do — pehle yahan success dikhta tha
      console.error("recordContactAttempt error:", e);
      message.error(e?.message || "Failed to log contact attempt. Please try again.");
    } finally {
      setSubmittingContact(false);
    }
  };

  // 🔄 PATCH Call: Update Task Status
  const handleStatusChange = async (taskId, newStatus, remarks = "") => {
    try {
      await fintechApi.updateTaskStatus(taskId, { status: newStatus, remarks });
      message.success(`Task status updated to ${newStatus}`);
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, status: newStatus, lastRemarks: remarks || t.lastRemarks } : t))
      );
      fetchCollectionData();
    } catch (e) {
      // Server par status nahi badla to screen par bhi mat badlo
      console.error("Status update failed:", e);
      message.error(e?.message || "Failed to update task status");
    }
  };

  // 🔄 PATCH Call from Inline Status Form
  const handleSaveStatusForm = async () => {
    if (!selectedTaskForStatus) return;
    setSubmittingStatus(true);
    try {
      const payload = {
        status: statusForm.status,
        remarks: statusForm.remarks || `Status updated to ${statusForm.status}`,
      };
      await fintechApi.updateTaskStatus(selectedTaskForStatus.id, payload);
      message.success(`Task status updated to ${statusForm.status} successfully!`);
      setTasks((prev) =>
        prev.map((t) =>
          t.id === selectedTaskForStatus.id
            ? {
                ...t,
                status: statusForm.status,
                lastRemarks: payload.remarks,
              }
            : t
        )
      );
      setSelectedTaskForStatus(null);
      setActiveTab("overview");
      fetchCollectionData();
    } catch (e) {
      console.error("Status update failed:", e);
      message.error(e?.message || "Failed to update task status");
    } finally {
      setSubmittingStatus(false);
    }
  };

  // 📥 Client-side CSV — sirf abhi screen par jo tasks/filters lage hain wahi jaate hain
  const handleExportCSV = () => {
    if (!filteredTasks.length) {
      message.warning("No collection tasks in the current view to export");
      return;
    }
    const headers = [
      "Task ID",
      "Borrower Name",
      "Phone",
      "Loan Account",
      "Overdue Amount",
      "Aging / Bucket",
      "Assigned Agent",
      "Status",
      "Last Remarks",
    ];
    const rows = filteredTasks.map((t) => [
      t.id,
      `"${t.borrower || ""}"`,
      `"${t.phone || ""}"`,
      `"${t.loanAccount || ""}"`,
      t.overdueAmount || 0,
      `"${t.dueSince || ""}"`,
      `"${t.agent || ""}"`,
      `"${t.status || ""}"`,
      `"${(t.lastRemarks || "").replace(/"/g, '""')}"`,
    ]);
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `collections_tasks_${dayjs().format("YYYYMMDD_HHmm")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    message.success("Current view exported to CSV");
  };

  // 📈 Server-side Excel Report — is page par koi date range state nahi hai,
  // isliye current month (1st se month end tak) ka report maangte hain
  const handleExportReport = async () => {
    setExportingReport(true);
    try {
      const startDate = dayjs().startOf("month").format("YYYY-MM-DD");
      const endDate = dayjs().endOf("month").format("YYYY-MM-DD");

      // Blob aane ke baad hi success — API fail hui to catch me error dikhega
      const blob = await fintechApi.exportCollectionReport(startDate, endDate);
      triggerBlobDownload(blob, `collection-report-${dayjs().format("DD-MM-YYYY")}.xlsx`);
      message.success("Collection report exported successfully");
    } catch (e) {
      console.error("exportCollectionReport error:", e);
      message.error(e?.message || "Failed to export collection report");
    } finally {
      setExportingReport(false);
    }
  };

  // 🔍 Filtered Tasks List
  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      const q = searchQuery.toLowerCase().trim();
      const matchQuery =
        !q ||
        t.borrower?.toLowerCase().includes(q) ||
        t.phone?.toLowerCase().includes(q) ||
        t.loanAccount?.toLowerCase().includes(q) ||
        t.agent?.toLowerCase().includes(q) ||
        t.lastRemarks?.toLowerCase().includes(q);

      const matchStatus =
        statusFilter === "ALL" || (t.status || "PENDING") === statusFilter;

      const matchBucket =
        bucketFilter === "ALL" ||
        t.dueSince?.toLowerCase().includes(bucketFilter.toLowerCase()) ||
        t.bucket?.toLowerCase().includes(bucketFilter.toLowerCase());

      const matchAgent =
        agentFilter === "ALL" ||
        (t.agent || "").toLowerCase() === agentFilter.toLowerCase();

      return matchQuery && matchStatus && matchBucket && matchAgent;
    });
  }, [tasks, searchQuery, statusFilter, bucketFilter, agentFilter]);

  // Unique agents list for filter dropdown
  const uniqueAgents = useMemo(() => {
    const set = new Set();
    tasks.forEach((t) => {
      if (t.agent) set.add(t.agent);
    });
    agentPerformance.forEach((a) => {
      if (a.name) set.add(a.name);
    });
    return Array.from(set);
  }, [tasks, agentPerformance]);

  return (
    <div className="space-y-6 pb-20">
      {/* ── Top Navigation Bar & View Tabs ── */}
      <div className="bg-white p-4 md:p-5 rounded-2xl border border-[#e5e7eb] shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-md transition-transform hover:scale-105"
            style={{ backgroundColor: primaryColor }}
          >
            <PhoneCall className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl md:text-2xl font-black text-gray-900 tracking-tight">
                Collections & Recovery Control Center
              </h1>
              <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                <Sparkles className="w-3 h-3 text-emerald-600" />
                Live Desk
              </span>
            </div>
            <p className="text-xs md:text-sm text-gray-500 mt-0.5">
              Overdue DPD tracking, telecalling queue, and automated Promise-to-Pay workflow
            </p>
          </div>
        </div>

        {/* View Switcher Tabs (Single Page, Zero Popups) */}
        <div className="flex items-center flex-wrap gap-2">
          <div className="flex bg-gray-100 p-1 rounded-xl border border-gray-200 text-xs font-bold">
            <button
              onClick={() => {
                setActiveTab("overview");
                setSelectedTaskForCall(null);
                setSelectedTaskForStatus(null);
                setHistoryTask(null);
              }}
              className={`px-4 py-2 rounded-lg transition-all flex items-center gap-1.5 ${
                activeTab === "overview"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <Layers className="w-3.5 h-3.5" />
              <span>Recovery Desk</span>
            </button>

            <button
              onClick={() => {
                setActiveTab("create-task");
                setSelectedTaskForCall(null);
                setSelectedTaskForStatus(null);
                setHistoryTask(null);
              }}
              className={`px-4 py-2 rounded-lg transition-all flex items-center gap-1.5 ${
                activeTab === "create-task"
                  ? "bg-[#2e856e] text-white shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              style={activeTab === "create-task" ? { backgroundColor: primaryColor } : {}}
            >
              <Plus className="w-3.5 h-3.5" />
              <span>+ Assign New Task</span>
            </button>
          </div>

          {activeTab === "overview" && (
            <>
              {/* Jo abhi table me dikh raha hai (filters ke saath) wahi CSV me */}
              <button
                onClick={handleExportCSV}
                className="px-3.5 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl border border-gray-200 transition-all shadow-sm flex items-center gap-1.5 text-xs font-semibold"
                title="Download the tasks currently shown in this queue as CSV"
              >
                <Download className="w-3.5 h-3.5 text-gray-500" />
                <span>Export Current View (CSV)</span>
              </button>

              {/* Backend se poore month ka collection report (Excel) */}
              <button
                onClick={handleExportReport}
                disabled={exportingReport}
                className="px-3.5 py-2 bg-[#2e856e]/5 hover:bg-[#2e856e]/10 text-[#2e856e] rounded-xl border border-[#2e856e]/30 transition-all shadow-sm flex items-center gap-1.5 text-xs font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                title="Download this month's collection report from server as Excel"
              >
                {exportingReport ? (
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                <span>{exportingReport ? "Exporting..." : "Export Report (Excel)"}</span>
              </button>

              <button
                onClick={fetchCollectionData}
                disabled={loading}
                className="p-2 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl border border-gray-200 transition-all shadow-sm flex items-center gap-1.5 text-xs font-semibold"
                title="Refresh dashboard data"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-[#2e856e]" : ""}`} />
                <span className="hidden sm:inline">Sync Desk</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          VIEW 1: FULL SINGLE PAGE FORM FOR ASSIGNING NEW RECOVERY TASK
      ══════════════════════════════════════════════════════════════════ */}
      {activeTab === "create-task" && (
        <div className="bg-white p-6 md:p-8 rounded-2xl border border-[#e5e7eb] shadow-sm space-y-6">
          <div className="flex items-center justify-between pb-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setActiveTab("overview")}
                className="p-2 rounded-xl bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-600 transition-all"
                title="Back to Recovery Desk"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <h2 className="text-lg md:text-xl font-bold text-gray-900">
                  Assign New Recovery / Collection Case
                </h2>
                <p className="text-xs text-gray-500">
                  Enter borrower delinquency details and assign to a recovery telecaller
                </p>
              </div>
            </div>

            <span className="px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-bold">
              POST /collections/tasks
            </span>
          </div>

          <form onSubmit={handleCreateTask} className="space-y-6">
            {/* Section 1: Borrower Profile */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-bold text-gray-900 pb-1 border-b border-gray-100">
                <Users className="w-4 h-4 text-[#2e856e]" />
                <span>1. Borrower Contact Profile</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
                <div>
                  <label className="block font-bold text-gray-700 mb-1.5">
                    Borrower Full Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    size="large"
                    placeholder="e.g. Ramesh Kumar Verma"
                    value={newTaskForm.borrower}
                    onChange={(e) => setNewTaskForm((p) => ({ ...p, borrower: e.target.value }))}
                    className="rounded-xl"
                  />
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-1.5">
                    Primary Phone Number <span className="text-red-500">*</span>
                  </label>
                  <Input
                    size="large"
                    placeholder="e.g. 9876543210"
                    value={newTaskForm.phone}
                    onChange={(e) => setNewTaskForm((p) => ({ ...p, phone: e.target.value }))}
                    className="rounded-xl"
                  />
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-1.5">
                    Alternative Phone / WhatsApp
                  </label>
                  <Input
                    size="large"
                    placeholder="e.g. 9811223344"
                    value={newTaskForm.altPhone}
                    onChange={(e) => setNewTaskForm((p) => ({ ...p, altPhone: e.target.value }))}
                    className="rounded-xl"
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Loan & Overdue Financials */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-bold text-gray-900 pb-1 border-b border-gray-100">
                <DollarSign className="w-4 h-4 text-rose-600" />
                <span>2. Loan & Delinquency Details</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
                <div>
                  <label className="block font-bold text-gray-700 mb-1.5">
                    Loan Account Number <span className="text-red-500">*</span>
                  </label>
                  <Input
                    size="large"
                    placeholder="e.g. LA-9021"
                    value={newTaskForm.loanAccount}
                    onChange={(e) => setNewTaskForm((p) => ({ ...p, loanAccount: e.target.value }))}
                    className="rounded-xl font-mono font-bold"
                  />
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-1.5">
                    Total Overdue Amount (₹) <span className="text-red-500">*</span>
                  </label>
                  <InputNumber
                    size="large"
                    className="w-full rounded-xl font-black text-rose-700"
                    placeholder="₹ 25,000"
                    min={1}
                    value={newTaskForm.overdueAmount}
                    onChange={(val) => setNewTaskForm((p) => ({ ...p, overdueAmount: val }))}
                  />
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-1.5">
                    DPD Aging Bucket <span className="text-red-500">*</span>
                  </label>
                  <Select
                    size="large"
                    className="w-full rounded-xl"
                    value={newTaskForm.dueSince}
                    onChange={(val) => setNewTaskForm((p) => ({ ...p, dueSince: val }))}
                    options={[
                      { value: "15 Days (Bucket 0)", label: "0-30 Days (Bucket 0)" },
                      { value: "45 Days (Bucket 1)", label: "31-60 Days (Bucket 1)" },
                      { value: "75 Days (Bucket 2)", label: "61-90 Days (Bucket 2)" },
                      { value: "90+ Days (NPA / Default)", label: "90+ Days (NPA / Default)" },
                    ]}
                  />
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-1.5">
                    Recovery Priority
                  </label>
                  <Select
                    size="large"
                    className="w-full rounded-xl font-bold"
                    value={newTaskForm.priority}
                    onChange={(val) => setNewTaskForm((p) => ({ ...p, priority: val }))}
                    options={[
                      { value: "HIGH", label: "🔥 High (Immediate Call)" },
                      { value: "MEDIUM", label: "⚡ Medium (Normal Queue)" },
                      { value: "LOW", label: "🕒 Low (Courtesy Reminder)" },
                    ]}
                  />
                </div>
              </div>
            </div>

            {/* Section 3: Telecaller Assignment & Strategy */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-bold text-gray-900 pb-1 border-b border-gray-100">
                <UserCheck className="w-4 h-4 text-blue-600" />
                <span>3. Telecaller Assignment & Notes</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                <div>
                  <label className="block font-bold text-gray-700 mb-1.5">
                    Assigned Telecaller / Recovery Officer
                  </label>
                  <Input
                    size="large"
                    placeholder="e.g. Rahul Sharma"
                    value={newTaskForm.agent}
                    onChange={(e) => setNewTaskForm((p) => ({ ...p, agent: e.target.value }))}
                    className="rounded-xl"
                  />
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-1.5">
                    Initial Initial Status
                  </label>
                  <Select
                    size="large"
                    className="w-full rounded-xl"
                    value={newTaskForm.status}
                    onChange={(val) => setNewTaskForm((p) => ({ ...p, status: val }))}
                    options={[
                      { value: "PENDING", label: "Pending (Needs Call)" },
                      { value: "CALLED", label: "Called (Contact established)" },
                      { value: "PROMISE_TO_PAY", label: "Promise to Pay (PTP)" },
                      { value: "ESCALATED", label: "Escalated to Field / Legal" },
                    ]}
                  />
                </div>
              </div>

              <div className="text-xs">
                <label className="block font-bold text-gray-700 mb-1.5">
                  Initial Notes / Reason for Overdue
                </label>
                <Input.TextArea
                  rows={3}
                  placeholder="Enter any existing borrower context, reason for EMI bounce, salary delay..."
                  value={newTaskForm.remarks}
                  onChange={(e) => setNewTaskForm((p) => ({ ...p, remarks: e.target.value }))}
                  className="rounded-xl"
                />
              </div>
            </div>

            {/* Form Submit & Cancel Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setActiveTab("overview")}
                className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold text-xs transition-all"
              >
                Cancel & Return
              </button>

              <button
                type="submit"
                disabled={submittingNewTask}
                className="px-6 py-2.5 bg-[#2e856e] hover:bg-[#246b58] text-white rounded-xl font-bold text-xs shadow-md transition-all flex items-center gap-2 active:scale-95 disabled:opacity-50"
                style={{ backgroundColor: primaryColor }}
              >
                {submittingNewTask ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                <span>Save & Assign Task</span>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          VIEW 2: FULL SINGLE PAGE FORM FOR LOGGING CALL DISPOSITION
      ══════════════════════════════════════════════════════════════════ */}
      {activeTab === "log-call" && selectedTaskForCall && (
        <div className="bg-white p-6 md:p-8 rounded-2xl border border-[#e5e7eb] shadow-sm space-y-6">
          <div className="flex items-center justify-between pb-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setSelectedTaskForCall(null);
                  setActiveTab("overview");
                }}
                className="p-2 rounded-xl bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-600 transition-all"
                title="Back to Recovery Desk"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <h2 className="text-lg md:text-xl font-bold text-gray-900">
                  Log Telecall Contact Attempt & Disposition
                </h2>
                <p className="text-xs text-gray-500">
                  Record call outcome, customer feedback, and Promise to Pay (PTP) commitments
                </p>
              </div>
            </div>

            <span className="px-3 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs font-bold">
              POST /collections/tasks/:taskId/contact-attempt
            </span>
          </div>

          {/* Borrower Banner */}
          <div className="p-4 bg-gray-50 rounded-2xl border border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
            <div>
              <span className="text-[10px] font-bold uppercase text-gray-400">Borrower Account</span>
              <div className="font-black text-gray-900 text-base">{selectedTaskForCall.borrower}</div>
              <div className="text-gray-500 flex items-center gap-3 mt-1">
                <span>Phone: <b className="font-mono text-gray-800">{selectedTaskForCall.phone}</b></span>
                <span>•</span>
                <span>Loan A/C: <b className="font-mono text-gray-800">{selectedTaskForCall.loanAccount}</b></span>
              </div>
            </div>

            <div className="text-left sm:text-right">
              <span className="text-[10px] font-bold uppercase text-rose-500">Total Demand Overdue</span>
              <div className="font-black text-rose-700 text-xl">{formatINR(selectedTaskForCall.overdueAmount)}</div>
              <span className="inline-block px-2 py-0.5 rounded bg-rose-100/70 text-rose-800 text-[10px] font-bold mt-0.5">
                {selectedTaskForCall.dueSince || "Bucket 0"}
              </span>
            </div>
          </div>

          {/* Form Fields */}
          <div className="space-y-4 text-xs">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-bold text-gray-700 mb-1.5">
                  Contact Mode <span className="text-red-500">*</span>
                </label>
                <Select
                  size="large"
                  className="w-full rounded-xl"
                  value={contactForm.contactMode}
                  onChange={(val) => setContactForm((prev) => ({ ...prev, contactMode: val }))}
                  options={[
                    { value: "PHONE_CALL", label: "📞 Phone Call" },
                    { value: "WHATSAPP", label: "💬 WhatsApp Message" },
                    { value: "SMS", label: "📩 SMS Reminder" },
                    { value: "FIELD_VISIT", label: "🏠 Field Visit" },
                  ]}
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1.5">
                  Call Disposition Outcome <span className="text-red-500">*</span>
                </label>
                <Select
                  size="large"
                  className="w-full rounded-xl font-bold"
                  value={contactForm.disposition}
                  onChange={(val) => setContactForm((prev) => ({ ...prev, disposition: val }))}
                  options={[
                    { value: "PROMISED_TO_PAY", label: "✅ Promise to Pay (PTP Scheduled)" },
                    { value: "PAID_ONLINE", label: "💳 Customer claims already paid" },
                    { value: "CALL_LATER", label: "🕒 Customer requested callback" },
                    { value: "RINGING_NO_ANSWER", label: "📵 Ringing / No Answer" },
                    { value: "SWITCHED_OFF", label: "📴 Switched Off / Not Reachable" },
                    { value: "DISPUTE_RAISED", label: "⚠️ Dispute / Calculation query" },
                    { value: "REFUSED_TO_PAY", label: "❌ Customer refused payment" },
                  ]}
                />
              </div>
            </div>

            {contactForm.disposition === "PROMISED_TO_PAY" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-amber-50/60 rounded-2xl border border-amber-200">
                <div>
                  <label className="block font-bold text-amber-900 mb-1.5">
                    Promised Amount to Pay (₹)
                  </label>
                  <InputNumber
                    size="large"
                    className="w-full rounded-xl font-black text-amber-900"
                    placeholder="₹ 15,000"
                    min={1}
                    value={contactForm.ptpAmount}
                    onChange={(val) => setContactForm((prev) => ({ ...prev, ptpAmount: val }))}
                  />
                </div>

                <div>
                  <label className="block font-bold text-amber-900 mb-1.5">
                    PTP Committed Follow-up Date
                  </label>
                  <DatePicker
                    size="large"
                    className="w-full rounded-xl"
                    onChange={(date) => setContactForm((prev) => ({ ...prev, nextFollowUpDate: date }))}
                    format="DD/MM/YYYY"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block font-bold text-gray-700 mb-1.5">
                Call Summary / Detailed Telecaller Notes
              </label>
              <Input.TextArea
                rows={4}
                placeholder="Enter customer response, reasons given for delay, payment commitment date, next action required..."
                value={contactForm.remarks}
                onChange={(e) => setContactForm((prev) => ({ ...prev, remarks: e.target.value }))}
                className="rounded-xl"
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  setSelectedTaskForCall(null);
                  setActiveTab("overview");
                }}
                className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold text-xs transition-all"
              >
                Cancel & Return
              </button>

              <button
                type="button"
                onClick={handleSaveContactAttempt}
                disabled={submittingContact}
                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-xs shadow-md transition-all flex items-center gap-2 active:scale-95 disabled:opacity-50"
              >
                {submittingContact ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                <span>Save Call Feedback</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          VIEW 3: FULL SINGLE PAGE FORM FOR EDITING STATUS (PATCH)
      ══════════════════════════════════════════════════════════════════ */}
      {activeTab === "edit-status" && selectedTaskForStatus && (
        <div className="bg-white p-6 md:p-8 rounded-2xl border border-[#e5e7eb] shadow-sm space-y-6">
          <div className="flex items-center justify-between pb-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setSelectedTaskForStatus(null);
                  setActiveTab("overview");
                }}
                className="p-2 rounded-xl bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-600 transition-all"
                title="Back to Recovery Desk"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div>
                <h2 className="text-lg md:text-xl font-bold text-gray-900">
                  Update Task Recovery Status & Resolution Notes
                </h2>
                <p className="text-xs text-gray-500">
                  Modify the lifecycle status of this overdue case
                </p>
              </div>
            </div>

            <span className="px-3 py-1 bg-purple-50 text-purple-700 border border-purple-200 rounded-full text-xs font-bold">
              PATCH /collections/tasks/:taskId/status
            </span>
          </div>

          <div className="p-4 bg-gray-50 rounded-2xl border border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
            <div>
              <div className="font-black text-gray-900 text-base">{selectedTaskForStatus.borrower}</div>
              <div className="text-gray-500 mt-0.5">
                Loan Account: <b className="font-mono text-gray-800">{selectedTaskForStatus.loanAccount}</b> | Phone:{" "}
                <b className="font-mono text-gray-800">{selectedTaskForStatus.phone}</b>
              </div>
            </div>

            <div className="text-left sm:text-right">
              <span className="text-[10px] font-bold uppercase text-gray-400">Overdue Amount</span>
              <div className="font-black text-rose-700 text-lg">{formatINR(selectedTaskForStatus.overdueAmount)}</div>
            </div>
          </div>

          <div className="space-y-4 text-xs">
            <div>
              <label className="block font-bold text-gray-700 mb-1.5">
                Select New Status <span className="text-red-500">*</span>
              </label>
              <Select
                size="large"
                className="w-full rounded-xl font-bold"
                value={statusForm.status}
                onChange={(val) => setStatusForm((p) => ({ ...p, status: val }))}
                options={[
                  { value: "PENDING", label: "Pending (Needs Follow-up)" },
                  { value: "CALLED", label: "Called (Contact Made)" },
                  { value: "PROMISE_TO_PAY", label: "Promise to Pay (PTP Committed)" },
                  { value: "ESCALATED", label: "Escalated (Field Visit / Legal)" },
                  { value: "RESOLVED", label: "Resolved (Payment Cleared)" },
                ]}
              />
            </div>

            <div>
              <label className="block font-bold text-gray-700 mb-1.5">
                Status Update Remarks / Action Taken
              </label>
              <Input.TextArea
                rows={3}
                placeholder="Enter remarks explaining reason for status change or resolution details..."
                value={statusForm.remarks}
                onChange={(e) => setStatusForm((p) => ({ ...p, remarks: e.target.value }))}
                className="rounded-xl"
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  setSelectedTaskForStatus(null);
                  setActiveTab("overview");
                }}
                className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold text-xs transition-all"
              >
                Cancel & Return
              </button>

              <button
                type="button"
                onClick={handleSaveStatusForm}
                disabled={submittingStatus}
                className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs shadow-md transition-all flex items-center gap-2 active:scale-95 disabled:opacity-50"
              >
                {submittingStatus ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                <span>Update Status</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          VIEW 4: MAIN RECOVERY DESK & CALLING QUEUE TABLE
      ══════════════════════════════════════════════════════════════════ */}
      {activeTab === "overview" && (
        <>
          {/* ── Top Summary 4 Metric Cards ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Overdue */}
            <div className="p-5 bg-gradient-to-br from-red-50/50 to-white rounded-2xl border border-red-100 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
              <div className="flex items-center justify-between text-xs font-bold uppercase text-red-600">
                <span>Total Overdue Demand</span>
                <div className="w-8 h-8 rounded-xl bg-red-100/70 flex items-center justify-center text-red-600">
                  <AlertTriangle className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl md:text-3xl font-black text-red-700 mt-2">
                {formatINR(stats.totalOverdue)}
              </div>
              <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1">
                <span className="font-semibold text-red-600">Across all</span> aging buckets
              </p>
            </div>

            {/* Collected Today */}
            <div className="p-5 bg-gradient-to-br from-emerald-50/50 to-white rounded-2xl border border-emerald-100 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
              <div className="flex items-center justify-between text-xs font-bold uppercase text-emerald-600">
                <span>Collected Today</span>
                <div className="w-8 h-8 rounded-xl bg-emerald-100/70 flex items-center justify-center text-emerald-600">
                  <CheckCircle2 className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl md:text-3xl font-black text-emerald-700 mt-2">
                {formatINR(stats.collectedToday)}
              </div>
              <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1">
                <span className="font-semibold text-emerald-600">Cleared</span> recoveries today
              </p>
            </div>

            {/* Pending Call Tasks */}
            <div className="p-5 bg-gradient-to-br from-blue-50/50 to-white rounded-2xl border border-blue-100 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
              <div className="flex items-center justify-between text-xs font-bold uppercase text-blue-600">
                <span>Pending Call Tasks</span>
                <div className="w-8 h-8 rounded-xl bg-blue-100/70 flex items-center justify-center text-blue-600">
                  <Clock className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl md:text-3xl font-black text-blue-700 mt-2">
                {stats.pendingTasks || tasks.filter((t) => t.status === "PENDING").length}
              </div>
              <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1">
                <span className="font-semibold text-blue-600">Assigned</span> for agent follow-up
              </p>
            </div>

            {/* Promise to Pay (PTP) */}
            <div className="p-5 bg-gradient-to-br from-amber-50/50 to-white rounded-2xl border border-amber-100 shadow-sm relative overflow-hidden group hover:shadow-md transition-all">
              <div className="flex items-center justify-between text-xs font-bold uppercase text-amber-600">
                <span>Promise to Pay (PTP)</span>
                <div className="w-8 h-8 rounded-xl bg-amber-100/70 flex items-center justify-center text-amber-600">
                  <TrendingUp className="w-4 h-4" />
                </div>
              </div>
              <div className="text-2xl md:text-3xl font-black text-amber-700 mt-2">
                {stats.promiseToPay || tasks.filter((t) => t.status === "PROMISE_TO_PAY").length}
              </div>
              <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1">
                <span className="font-semibold text-amber-600">Commitments</span> scheduled
              </p>
            </div>
          </div>

          {/* ── Two Columns: Bucket Matrix & Agent Performance ── */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left: DPD Aging Bucket Matrix */}
            <div className="lg:col-span-7 bg-white p-5 md:p-6 rounded-2xl border border-[#e5e7eb] shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-base font-bold text-gray-900">
                    DPD Aging Bucket Summary
                  </h2>
                  <p className="text-xs text-gray-500">
                    Demanded vs recovered amount categorized by aging delinquency
                  </p>
                </div>
                <span className="text-xs font-semibold px-2.5 py-1 bg-gray-100 text-gray-700 rounded-lg">
                  PAR Portfolio
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/80 text-gray-600 uppercase font-bold">
                      <th className="py-3 px-3.5 rounded-l-xl">Aging Bucket</th>
                      <th className="py-3 px-2.5 text-center">Active Loans</th>
                      <th className="py-3 px-3.5 text-right">Overdue Demand</th>
                      <th className="py-3 px-3.5 text-right">Recovered</th>
                      <th className="py-3 px-3.5 text-right rounded-r-xl">PAR %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {buckets.map((b, idx) => (
                      <tr key={idx} className="hover:bg-gray-50/60 transition-colors">
                        <td className="py-3 px-3.5 font-bold text-gray-800 flex items-center gap-2">
                          <span
                            className={`w-2 h-2 rounded-full ${
                              idx === 0
                                ? "bg-amber-400"
                                : idx === 1
                                ? "bg-orange-500"
                                : idx === 2
                                ? "bg-rose-500"
                                : "bg-red-700"
                            }`}
                          />
                          <span>{b.bucket}</span>
                        </td>
                        <td className="py-3 px-2.5 text-center font-semibold text-gray-600">{b.count}</td>
                        <td className="py-3 px-3.5 text-right font-black text-gray-900">{formatINR(b.overdue)}</td>
                        <td className="py-3 px-3.5 text-right font-bold text-emerald-700">{formatINR(b.collected)}</td>
                        <td className="py-3 px-3.5 text-right font-black text-rose-600">{b.par}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Right: Agent Performance Table */}
            <div className="lg:col-span-5 bg-white p-5 md:p-6 rounded-2xl border border-[#e5e7eb] shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-base font-bold text-gray-900">
                    Telecaller Recovery Board
                  </h2>
                  <p className="text-xs text-gray-500">
                    Recovery throughput and collection rate
                  </p>
                </div>
                <Users className="w-4 h-4 text-gray-400" />
              </div>

              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                {agentPerformance.length === 0 ? (
                  <div className="text-center py-8 text-xs text-gray-400">
                    No active telecallers assigned yet
                  </div>
                ) : (
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50/80 text-gray-600 uppercase font-bold sticky top-0 bg-gray-50">
                        <th className="py-2.5 px-3 rounded-l-lg">Telecaller</th>
                        <th className="py-2.5 px-2 text-center">Tasks</th>
                        <th className="py-2.5 px-3 text-right">Collected</th>
                        <th className="py-2.5 px-3 text-right rounded-r-lg">Rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {agentPerformance.map((a, idx) => (
                        <tr key={idx} className="hover:bg-gray-50/60">
                          <td className="py-2.5 px-3 font-semibold text-gray-800">{a.name}</td>
                          <td className="py-2.5 px-2 text-center text-gray-600">
                            {a.completed ?? a.collectedCount ?? 0}/{a.assigned ?? 0}
                          </td>
                          <td className="py-2.5 px-3 text-right font-bold text-emerald-700">
                            {formatINR(a.collected)}
                          </td>
                          <td className="py-2.5 px-3 text-right font-bold text-[#2e856e]">
                            {a.rate || "0%"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {/* ── Active Collection Task Queue ── */}
          <div className="bg-white p-5 md:p-6 rounded-2xl border border-[#e5e7eb] shadow-sm space-y-4">
            {/* Header & Search / Filters */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-2 border-b border-gray-100">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base md:text-lg font-bold text-gray-900">
                    Active Collection & Calling Task Queue
                  </h2>
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-gray-100 text-gray-700">
                    {filteredTasks.length} Cases
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  Assigned overdue accounts requiring telecalling, field visits, or legal escalation
                </p>
              </div>

              {/* Quick Filters */}
              <div className="flex items-center flex-wrap gap-2 text-xs">
                {/* Search Input */}
                <div className="relative min-w-[200px]">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search borrower, phone, A/C..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-3 py-1.5 bg-gray-50 rounded-xl border border-gray-200 text-xs focus:outline-none focus:ring-1 focus:ring-[#2e856e]"
                  />
                </div>

                {/* Status Filter */}
                <Select
                  className="w-36 rounded-xl"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={[
                    { value: "ALL", label: "All Statuses" },
                    { value: "PENDING", label: "Pending" },
                    { value: "CALLED", label: "Called" },
                    { value: "PROMISE_TO_PAY", label: "Promise to Pay (PTP)" },
                    { value: "ESCALATED", label: "Escalated" },
                    { value: "RESOLVED", label: "Resolved" },
                  ]}
                />

                {/* Bucket Filter */}
                <Select
                  className="w-36 rounded-xl"
                  value={bucketFilter}
                  onChange={setBucketFilter}
                  options={[
                    { value: "ALL", label: "All Buckets" },
                    { value: "Bucket 0", label: "0-30 Days (Bucket 0)" },
                    { value: "Bucket 1", label: "31-60 Days (Bucket 1)" },
                    { value: "Bucket 2", label: "61-90 Days (Bucket 2)" },
                    { value: "NPA", label: "90+ Days (NPA)" },
                  ]}
                />

                {/* Agent Filter */}
                {uniqueAgents.length > 0 && (
                  <Select
                    className="w-36 rounded-xl"
                    value={agentFilter}
                    onChange={setAgentFilter}
                    options={[
                      { value: "ALL", label: "All Telecallers" },
                      ...uniqueAgents.map((ag) => ({ value: ag, label: ag })),
                    ]}
                  />
                )}
              </div>
            </div>

            {/* Task Queue Table */}
            <div className="overflow-x-auto">
              {filteredTasks.length === 0 ? (
                <div className="text-center py-12 text-sm text-gray-400">
                  <PhoneCall className="w-8 h-8 mx-auto text-gray-300 mb-2 opacity-50" />
                  No collection tasks match your selected filter criteria
                </div>
              ) : (
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/80 text-gray-600 uppercase font-bold">
                      <th className="py-3 px-3.5 rounded-l-xl">Borrower Profile</th>
                      <th className="py-3 px-3.5">Loan Account</th>
                      <th className="py-3 px-3.5 text-right">Overdue Amount</th>
                      <th className="py-3 px-3.5">Aging / Bucket</th>
                      <th className="py-3 px-3.5">Assigned Agent</th>
                      <th className="py-3 px-3.5 text-center">Status</th>
                      <th className="py-3 px-3.5">Last Remarks & Notes</th>
                      <th className="py-3 px-3.5 text-center rounded-r-xl">Quick Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredTasks.map((task) => (
                      <tr
                        key={task.id}
                        className="hover:bg-gray-50/70 transition-colors"
                      >
                        {/* Borrower Profile */}
                        <td className="py-3.5 px-3.5">
                          <div className="font-bold text-gray-900 text-xs">{task.borrower}</div>
                          <div className="text-gray-400 flex items-center gap-1.5 mt-0.5">
                            <Phone className="w-3 h-3 text-emerald-600" />
                            <span className="font-mono text-[11px]">{task.phone || "No phone"}</span>
                            {task.phone && (
                              <a
                                href={`https://wa.me/91${task.phone.replace(/[^0-9]/g, "")}?text=Dear%20${encodeURIComponent(
                                  task.borrower
                                )},%20your%20EMI%20is%20overdue%20for%20Loan%20Account%20${task.loanAccount}.%20Please%20pay%20immediately.`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-emerald-600 hover:text-emerald-700 ml-1"
                                title="Send WhatsApp Payment Reminder"
                              >
                                <MessageSquare className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        </td>

                        {/* Loan Account */}
                        <td className="py-3.5 px-3.5">
                          <button
                            onClick={() => navigate(`/fintech/loan-accounts/${task.loanAccount || task.id}`)}
                            className="font-mono font-bold text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                            title="Open Loan Details"
                          >
                            <span>{task.loanAccount || `LA-${task.id}`}</span>
                            <ExternalLink className="w-3 h-3 opacity-60" />
                          </button>
                        </td>

                        {/* Overdue Amount */}
                        <td className="py-3.5 px-3.5 text-right font-black text-rose-700 text-sm">
                          {formatINR(task.overdueAmount)}
                        </td>

                        {/* Aging / Bucket */}
                        <td className="py-3.5 px-3.5 font-semibold text-gray-700">
                          <span className="px-2 py-0.5 rounded-lg bg-gray-100 text-[11px]">
                            {task.dueSince || task.bucket || "Bucket 0"}
                          </span>
                        </td>

                        {/* Assigned Agent */}
                        <td className="py-3.5 px-3.5 font-medium text-gray-800">
                          <div className="flex items-center gap-1.5">
                            <div className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-[10px]">
                              {(task.agent || "U").charAt(0).toUpperCase()}
                            </div>
                            <span>{task.agent || "Unassigned"}</span>
                          </div>
                        </td>

                        {/* Status Dropdown */}
                        <td className="py-3.5 px-3.5 text-center">
                          <Select
                            size="small"
                            className="text-[11px] font-bold"
                            value={task.status || "PENDING"}
                            onChange={(newStatus) => handleStatusChange(task.id, newStatus)}
                            options={[
                              { value: "PENDING", label: "Pending" },
                              { value: "CALLED", label: "Called" },
                              { value: "PROMISE_TO_PAY", label: "Promise to Pay (PTP)" },
                              { value: "ESCALATED", label: "Escalated" },
                              { value: "RESOLVED", label: "Resolved" },
                            ]}
                            dropdownMatchSelectWidth={false}
                          />
                        </td>

                        {/* Last Remarks */}
                        <td className="py-3.5 px-3.5 text-gray-600 max-w-xs">
                          <div className="truncate font-medium" title={task.lastRemarks}>
                            {task.lastRemarks || "No call remarks logged"}
                          </div>
                        </td>

                        {/* Action Buttons */}
                        <td className="py-3.5 px-3.5 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            {/* Log Call Button (Switches to Single Page Call Disposition View) */}
                            <button
                              onClick={() => {
                                setSelectedTaskForCall(task);
                                setContactForm({
                                  disposition: "PROMISED_TO_PAY",
                                  contactMode: "PHONE_CALL",
                                  nextFollowUpDate: null,
                                  ptpAmount: task.overdueAmount,
                                  remarks: "",
                                });
                                setActiveTab("log-call");
                              }}
                              className="px-2.5 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 rounded-lg border border-emerald-200 font-bold transition-all flex items-center gap-1 text-[11px] hover:scale-105 active:scale-95"
                              title="Log Call / Contact Attempt (Open Full Form)"
                            >
                              <PhoneCall className="w-3 h-3 text-emerald-600" />
                              <span>Log Call</span>
                            </button>

                            {/* Edit Status Button (Switches to Single Page Status Edit View) */}
                            <button
                              onClick={() => {
                                setSelectedTaskForStatus(task);
                                setStatusForm({
                                  status: task.status || "PENDING",
                                  remarks: task.lastRemarks || "",
                                });
                                setActiveTab("edit-status");
                              }}
                              className="p-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg border border-blue-200 transition-all text-[11px]"
                              title="Update Status & Notes (Open Full Form)"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>

                            {/* View History Inline */}
                            <button
                              onClick={() => {
                                setHistoryTask(task);
                              }}
                              className="p-1.5 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-lg border border-gray-200 transition-all text-[11px]"
                              title="View History Notes"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Inline History Audit Trail Panel if a task is clicked */}
          {historyTask && (
            <div className="bg-white p-6 rounded-2xl border border-blue-200 shadow-sm space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <h3 className="text-base font-bold text-gray-900">
                    Recovery & Contact Audit History: {historyTask.borrower} ({historyTask.loanAccount})
                  </h3>
                </div>
                <button
                  onClick={() => setHistoryTask(null)}
                  className="text-xs font-bold text-gray-400 hover:text-gray-700 px-2 py-1 bg-gray-100 rounded-lg"
                >
                  Close History
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 space-y-1">
                  <span className="font-bold text-gray-700">Account Details:</span>
                  <div className="text-gray-600">Borrower: <b>{historyTask.borrower}</b></div>
                  <div className="text-gray-600">Phone: <b>{historyTask.phone}</b></div>
                  <div className="text-gray-600">Overdue Amount: <b className="text-rose-700">{formatINR(historyTask.overdueAmount)}</b></div>
                  <div className="text-gray-600">Current Status: <b className="text-emerald-700">{historyTask.status}</b></div>
                </div>

                <div className="p-3 bg-blue-50/60 rounded-xl border border-blue-100 space-y-1">
                  <span className="font-bold text-blue-900">Latest Remarks / Resolution:</span>
                  <p className="text-gray-800 text-xs font-medium">{historyTask.lastRemarks || "No previous log remarks."}</p>
                </div>
              </div>

              {historyTask.history && historyTask.history.length > 0 && (
                <div className="space-y-2 text-xs">
                  <h4 className="font-bold text-gray-700">Chronological Attempts Log:</h4>
                  <div className="space-y-2">
                    {historyTask.history.map((h, i) => (
                      <div key={i} className="p-2.5 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between">
                        <div>
                          <span className="font-bold text-gray-800">{h.disposition}</span>
                          <p className="text-gray-600 text-[11px] mt-0.5">{h.remarks}</p>
                        </div>
                        <span className="text-gray-400 text-[11px] font-mono">{h.date}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
